import { ApplicantPayment } from '@/app/apis/Applicants/schema'
import {
  PaymentSortBy,
  UseApplicantPaymentsParams,
} from '@/app/apis/Applicants/requests'
import PaymentStatusBadge from '@/app/common/components/PaymentStatusBadge'
import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { DownIcon, UpIcon } from '@/common/icons/general.tsx'
import { cn, currencyFormatter } from '@/common/utilities.ts'
import { formatDateToUsersTimezone } from '@/lib/utils'
import { AppRoutePaths } from '@/routing/route_paths'
import { RootState } from '@/storage/store.ts'
import { useGetApplicantPayments } from '@/app/pages/applicants/applicant_details/hooks/useGetApplicantPayments'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import DatePickerWithRange from '@/common/components/DateRangePicker'
import { DateRange } from 'react-day-picker'

const columnHelper = createColumnHelper<ApplicantPayment>()

const RECORDS_PER_PAGE = 10 as const

type PaymentSortableColumnLabel =
  | 'ID'
  | 'Applicant Name'
  | 'Student Name'
  | 'Type'
  | 'Provider'
  | 'Total Amount'
  | 'Invoice No'
  | 'Status'
  | 'Submitted At'

const columnLabelToSortKey: Record<PaymentSortableColumnLabel, PaymentSortBy> =
  {
    ID: 'payment_id',
    'Applicant Name': 'applicant_name',
    'Student Name': 'student_name',
    Type: 'transaction_type',
    Provider: 'provider_name',
    'Total Amount': 'total_amount',
    'Invoice No': 'invoice_quote',
    Status: 'status',
    'Submitted At': 'created_datetime',
  }

function SortableHeader({
  name,
  sortBy,
  sortOrder,
  onSort,
}: {
  name: PaymentSortableColumnLabel
  sortBy: PaymentSortBy
  sortOrder: 'asc' | 'desc'
  onSort: (col: PaymentSortableColumnLabel) => void
}) {
  const isActive = columnLabelToSortKey[name] === sortBy
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 bg-transparent p-0 text-left"
      onClick={() => onSort(name)}
      aria-label={`Sort by ${name}`}
    >
      {name}
      <span className="inline-flex flex-col items-center leading-none">
        <UpIcon
          className={cn(
            'text-xs leading-none',
            isActive && sortOrder === 'asc' ? 'text-gray-900' : 'text-gray-400'
          )}
        />
        <DownIcon
          className={cn(
            '-mt-1 text-xs leading-none',
            isActive && sortOrder === 'desc' ? 'text-gray-900' : 'text-gray-400'
          )}
        />
      </span>
    </button>
  )
}

export default function PaymentsModulePage() {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>(
    format(new Date(), 'hh:mm:ss a')
  )
  const [sortBy, setSortBy] = useState<PaymentSortBy>('created_datetime')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [dateRange, setDateRange] = useState<DateRange | undefined>()

  const navigate = useNavigate()

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, selectedProgramYear?.id, dateRange?.from, dateRange?.to])

  const applicantPaymentsParams: UseApplicantPaymentsParams = dateRange?.from
    ? {
        page: currentPage,
        perPage: recordsPerPage,
        search: debouncedSearch,
        programId: selectedProgramYear?.id,
        sortBy,
        sortOrder,
        startDate: dateRange.from,
        endDate: dateRange.to,
      }
    : {
        page: currentPage,
        perPage: recordsPerPage,
        search: debouncedSearch,
        programId: selectedProgramYear?.id,
        sortBy,
        sortOrder,
      }

  const { data, isLoading, refetch } = useGetApplicantPayments(
    applicantPaymentsParams
  )

  const payments = data?.data || []
  const totalRows = data?.total || 0
  const lastPage = data?.last_page || 1

  const handlePageChange = (page: number) => setCurrentPage(page)

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
    setCurrentPage(1)
  }

  const handleRefresh = () => {
    refetch().then(() => setLastUpdatedAt(format(new Date(), 'hh:mm:ss a')))
  }

  const handleSort = (col: PaymentSortableColumnLabel) => {
    const key = columnLabelToSortKey[col]
    const newOrder =
      key === sortBy ? (sortOrder === 'desc' ? 'asc' : 'desc') : 'asc'
    setCurrentPage(1)
    setSortBy(key)
    setSortOrder(newOrder)
  }

  const columns = [
    columnHelper.accessor('payment_id', {
      id: 'payment_id',
      header: () => (
        <SortableHeader
          name="ID"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="w-3/4 sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span>{row.payment_id}</span>
                <span className="md:hidden">
                  <PaymentStatusBadge value={row.status} />
                </span>
              </div>
              <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                <div>{row.applicant_name}</div>
                <div>{(row.student_name ?? []).join(', ')}</div>
                <div>{row.transaction_type}</div>
                <div>
                  {row.provider_name
                    ? row.provider_id !== null
                      ? `${row.provider_name} (ID: ${row.provider_id})`
                      : row.provider_name
                    : row.provider_id !== null
                      ? `ID: ${row.provider_id}`
                      : ''}
                </div>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('applicant_name', {
      id: 'applicant_name',
      header: () => (
        <SortableHeader
          name="Applicant Name"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor((row) => (row.student_name ?? []).join(', '), {
      id: 'student_name',
      header: () => (
        <SortableHeader
          name="Student Name"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('transaction_type', {
      id: 'transaction_type',
      header: () => (
        <SortableHeader
          name="Type"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: (info) => <div>{info.getValue()}</div>,
    }),
    columnHelper.accessor('provider_name', {
      id: 'provider_name',
      header: () => (
        <SortableHeader
          name="Provider"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: (info) => {
        const row = info.row.original

        return (
          <div>
            {row.provider_name
              ? row.provider_id !== null
                ? `${row.provider_name} (ID: ${row.provider_id})`
                : row.provider_name
              : row.provider_id !== null
                ? `ID: ${row.provider_id}`
                : ''}
          </div>
        )
      },
    }),
    columnHelper.accessor('total_amount', {
      id: 'total_amount',
      header: () => (
        <SortableHeader
          name="Total Amount"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: (info) => <div>{currencyFormatter.format(info.getValue())}</div>,
    }),
    columnHelper.accessor('invoice_quote', {
      id: 'invoice_quote',
      header: () => (
        <SortableHeader
          name="Invoice No"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: (info) => (
        <div className="max-w-[5rem] truncate">{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <SortableHeader
          name="Status"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: (info) => (
        <div>
          <PaymentStatusBadge value={info.getValue()} />
        </div>
      ),
    }),
    columnHelper.accessor('created_datetime', {
      id: 'created_datetime',
      header: () => (
        <SortableHeader
          name="Submitted At"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
        />
      ),
      cell: (info) => {
        const lastDateTimeStr = info.getValue()
        return (
          <span>
            {lastDateTimeStr ? formatDateToUsersTimezone(lastDateTimeStr) : ''}
          </span>
        )
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">Action</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <Button
            onClick={() =>
              navigate(
                AppRoutePaths.payments.details.index.replace(
                  ':paymentId',
                  row.payment_id.toString()
                )
              )
            }
          >
            Review
          </Button>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: payments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: lastPage,
  })

  return (
    <section aria-labelledby="payments-list-title">
      <Card className="mt-6">
        <CardHeader className="flex justify-between items-center text-md font-semibold">
          <div
            id="payments-list-title"
            className="text-left text-lg md:text-md text-gray-900"
          >
            Payments
            <span className="hidden mt-1 w-15ch text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
              {`last updated at: ${lastUpdatedAt}`}
            </span>
          </div>
          <div>
            <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
              <DatePickerWithRange
                value={dateRange}
                onDateChange={setDateRange}
                label="Filter By Date"
              />
              <SearchInput
                id="table_search"
                placeholder="Search Payments"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                <Button
                  variant="outline"
                  className="max-w-12"
                  size="md"
                  aria-label="Refresh payments list"
                  disabled={isLoading}
                  onClick={handleRefresh}
                >
                  <CircleArrowsIcon
                    className={cn('text-base', isLoading && 'animate-spin')}
                  />
                </Button>
                <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                  {`last updated at: ${lastUpdatedAt}`}
                </span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={cn(
                        'w px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                        index !== 0 &&
                          index !== headerGroup.headers.length - 1 &&
                          'hidden md:table-cell',
                        index === headerGroup.headers.length - 1 && 'text-left'
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {isLoading ? (
              <tbody>
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              </tbody>
            ) : payments.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    No payments found
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell, index) => (
                      <td
                        key={cell.id}
                        className={cn(
                          'py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2',
                          index !== 0 &&
                            index !== row.getVisibleCells().length - 1 &&
                            'hidden md:table-cell',
                          index === row.getVisibleCells().length - 1 &&
                            'text-left'
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </CardContent>
        <CardFooter className="p-0 border-t border-transparent mt-5">
          <Pagination
            itemsPerPage={recordsPerPage}
            currentPage={currentPage}
            totalRows={totalRows}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        </CardFooter>
      </Card>
    </section>
  )
}
